/**
 * Public Markets Page ("Data Product" v3 · F system).
 * Full-featured public view of Nigerian local market prices with multi-select
 * filtering, price trends, market comparison, historical prices and a sortable,
 * paginated price-records table. Theme-aware via useThemeMode palette.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Tabs,
  Tab,
  Button,
  Autocomplete,
  Dialog,
  Tooltip as MuiTooltip,
  IconButton,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { fadeUp, MotionInView } from '../../utils/animations';
import { useThemeMode, numericSx, radius, MONO } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useMarkets, useStates, useCommodities, useCategories, useLocalPrices } from '../../services/queries';
import { activeCategories, isCommodityActive } from '../../utils/catalogStatus';
import { LoadingSkeleton, ExportButton, RangeLockWrap} from '../../components/common';
import SectionHeading from '../../components/public/SectionHeading';
import DeltaChip from '../../components/public/DeltaChip';
import PublicHero from '../../components/public/PublicHero';

// ── Iconify helpers ───────────────────────────────────────────────────────────
const iconifyFromSx = (sx = {}, fontSize) => {
  const sizeMap = { small: 18, medium: 24, large: 32, inherit: 24 };
  const size = sx.fontSize || (fontSize && sizeMap[fontSize]) || 20;
  return { width: size, height: size, color: sx.color };
};
const SearchIcon = ({ sx, fontSize }) => <Icon icon="solar:magnifer-outline" {...iconifyFromSx(sx, fontSize)} />;

// Price formatter (₦). Null is "no figure", not zero: printing ₦0 for a row
// we have no price for is a number we made up.
const formatPrice = (v) => (v == null ? '-' : `₦${Number(v).toLocaleString()}`);

// Price per kg, however the row carries it, or null when it carries neither.
// Written once because `p.pricePerKg || (p.price / (p.bagSize || 100))` is
// wrong in a quiet way when both are null: `null / 100` is 0, so the row
// claimed a real price of ₦0 per kg rather than an absent one.
const perKg = (p) => {
  if (p.pricePerKg != null) return p.pricePerKg;
  if (p.price == null) return null;
  return p.price / (p.bagSize || 100);
};

// Series ramp for the per-commodity trend lines (F palette, no purple/teal-bright).
const TREND_RAMP = ['#1FA254', '#0F2744', '#B45309', '#0E7490', '#374151', '#8B5E34'];
const TREND_RANGES = [
  { label: '3M', weeks: 13 },
  { label: '6M', weeks: 26 },
  { label: '1Y', weeks: 52 },
  { label: 'ALL', weeks: 999 },
];

// Format YYYY-MM-DD -> "18 Apr 2026"
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// A market belongs to a set of state filters when the set is empty (= all)
// or one of the selected state tokens is contained in the market's state name.
const marketInStates = (market, states) =>
  states.length === 0 || states.some((s) => market.state.toLowerCase().includes(s.toLowerCase()));

// Price-records table columns.
const PRICE_COLUMNS = [
  { id: 'date', label: 'Date', align: 'left' },
  { id: 'market', label: 'Market', align: 'left' },
  { id: 'state', label: 'State', align: 'left' },
  { id: 'commodity', label: 'Commodity', align: 'left' },
  { id: 'bagSize', label: 'Bag Size', align: 'right', numeric: true },
  { id: 'price', label: 'Price', align: 'right', numeric: true },
];

// ── Component ────────────────────────────────────────────────────────────────
function PublicMarketsInner({ markets, pilotStates, commodities, commodityCategories, localPrices }) {
  const { palette } = useThemeMode();

  // ── Filter state (multi-select · empty array = All) ─────────────────────────
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceUnit, setPriceUnit] = useState('bag');
  const [searchTerm, setSearchTerm] = useState('');
  const [histMarket, setHistMarket] = useState('MKT001');
  const [histCommodity, setHistCommodity] = useState('COM005');
  const [activeTab, setActiveTab] = useState(0);

  // ── Sorting + pagination (Price Records) ────────────────────────────────────
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // ── Option lists ────────────────────────────────────────────────────────────
  const stateOptions = useMemo(
    () => pilotStates.map((s) => ({ value: s.name.split(' ')[0], label: s.name })),
    [pilotStates]
  );
  const categoryOptions = useMemo(
    () => activeCategories(commodityCategories).map((c) => ({ value: c.id, label: c.name })),
    [commodityCategories]
  );

  const stateLabel = (v) => stateOptions.find((o) => o.value === v)?.label || v;
  const marketLabel = (v) => markets.find((m) => m.id === v)?.name || v;
  const categoryLabel = (v) => categoryOptions.find((o) => o.value === v)?.label || v;

  // Market options narrow to the selected states (empty = all markets).
  const filteredMarkets = useMemo(
    () => markets.filter((m) => marketInStates(m, selectedStates)),
    [markets, selectedStates]
  );

  // ── Filter mutations ────────────────────────────────────────────────────────
  // Applying states also drops any now-invalid market selections.
  const applyStates = (next) => {
    setSelectedStates(next);
    setSelectedMarkets((prev) =>
      prev.filter((id) => {
        const mk = markets.find((m) => m.id === id);
        return mk && marketInStates(mk, next);
      })
    );
  };
  const asArray = (value) => (typeof value === 'string' ? value.split(',') : value);
  const handleStatesChange = (value) => applyStates(asArray(value));
  const handleMarketsChange = (value) => setSelectedMarkets(asArray(value));
  const handleCategoriesChange = (value) => setSelectedCategories(asArray(value));
  const removeState = (v) => applyStates(selectedStates.filter((x) => x !== v));
  const removeMarket = (v) => setSelectedMarkets((prev) => prev.filter((x) => x !== v));
  const removeCategory = (v) => setSelectedCategories((prev) => prev.filter((x) => x !== v));

  const clearFilters = () => {
    setSelectedStates([]);
    setSelectedMarkets([]);
    setSelectedCategories([]);
    setSearchTerm('');
  };

  const hasActiveFilters =
    selectedStates.length > 0 ||
    selectedMarkets.length > 0 ||
    selectedCategories.length > 0 ||
    searchTerm !== '';

  // Reset to first page whenever the filter set changes.
  useEffect(() => {
    setPage(0);
  }, [selectedStates, selectedMarkets, selectedCategories, searchTerm]);

  // ── Derived data ────────────────────────────────────────────────────────────
  const filteredPrices = useMemo(() => {
    return localPrices.filter((price) => {
      const market = markets.find((m) => m.id === price.marketId);
      // Market gate: empty = all, else must include this record's market.
      if (selectedMarkets.length > 0 && !selectedMarkets.includes(price.marketId)) return false;
      // State gate: empty = all, else the market's state must be among selected.
      if (selectedStates.length > 0 && (!market || !marketInStates(market, selectedStates))) return false;
      // Category gate.
      if (selectedCategories.length > 0) {
        const commodity = commodities.find((c) => c.id === price.commodityId);
        if (!commodity || !selectedCategories.includes(commodity.category)) return false;
      }
      // Free-text search over commodity + market names.
      if (searchTerm) {
        const commodity = commodities.find((c) => c.id === price.commodityId);
        const lower = searchTerm.toLowerCase();
        return (
          commodity?.name.toLowerCase().includes(lower) ||
          market?.name.toLowerCase().includes(lower)
        );
      }
      return true;
    });
  }, [selectedStates, selectedMarkets, selectedCategories, searchTerm, localPrices, markets, commodities]);

  // ── Per-commodity price trend ──────────────────────────────────────────────
  // Averaging different commodities into one line hides real movement behind
  // record-mix changes, so each plotted line is a single commodity, always
  // normalised to ₦/kg (bag sizes differ per commodity). Default series = the
  // top 5 commodities by record coverage within the current filters; the
  // picker overrides. Missing weeks stay as gaps (connectNulls off).
  const [trendPick, setTrendPick] = useState([]); // commodity objects; empty = auto top-3
  const [trendGran, setTrendGran] = useState('W'); // 'W' | 'M'
  const [trendWeeks, setTrendWeeks] = useState(26);

  const trendOptions = useMemo(() => {
    const present = new Set(filteredPrices.map((p) => p.commodityId));
    return commodities.filter((c) => present.has(c.id));
  }, [filteredPrices, commodities]);

  // RETIRED commodities (inactive commodity or category): auto-picked chart
  // defaults skip them, and anywhere they still appear (explicit picks,
  // history rows) they are labeled '(retired)'.
  const retiredIds = useMemo(
    () => new Set(commodities.filter((c) => !isCommodityActive(c, commodityCategories)).map((c) => c.id)),
    [commodities, commodityCategories]
  );
  const retiredLabel = (c) => (retiredIds.has(c.id) ? `${c.name} (retired)` : c.name);

  const trend = useMemo(() => {
    if (filteredPrices.length === 0) return { rows: [], series: [] };
    let maxDate = null;
    for (const p of filteredPrices) if (!maxDate || p.date > maxDate) maxDate = p.date;
    const cutoff = trendWeeks >= 999
      ? null
      : new Date(new Date(maxDate).getTime() - trendWeeks * 7 * 86400000).toISOString().slice(0, 10);
    const inRange = cutoff ? filteredPrices.filter((p) => p.date >= cutoff) : filteredPrices;

    // Default = top 3 by coverage: enough signal to compare, calm enough to read.
    let ids = trendPick.map((c) => c.id);
    if (ids.length === 0) {
      const counts = {};
      inRange.forEach((p) => { counts[p.commodityId] = (counts[p.commodityId] || 0) + 1; });
      ids = Object.entries(counts)
        .filter(([id]) => !retiredIds.has(id))
        .sort((a, b) => b[1] - a[1]).slice(0, 3).map(([id]) => id);
    }
    const series = ids.map((id, i) => ({
      id,
      name: (commodities.find((c) => c.id === id)?.name || id) + (retiredIds.has(id) ? ' (retired)' : ''),
      color: TREND_RAMP[i % TREND_RAMP.length],
    }));

    const buckets = {};
    inRange.forEach((p) => {
      if (!ids.includes(p.commodityId)) return;
      const key = trendGran === 'M' ? p.date.slice(0, 7) : p.date;
      if (!buckets[key]) buckets[key] = { date: key };
      const value = perKg(p);
      if (value == null) return;  // not an observation of zero
      const cell = buckets[key][p.commodityId] || { t: 0, c: 0 };
      cell.t += value;
      cell.c += 1;
      buckets[key][p.commodityId] = cell;
    });
    const rows = Object.values(buckets)
      .map((b) => {
        // Real timestamps on X so data holes render as horizontal space,
        // not as adjacent points pretending to be consecutive.
        const row = { date: b.date, ts: new Date(trendGran === 'M' ? `${b.date}-01` : b.date).getTime() };
        ids.forEach((id) => { if (b[id]?.c) row[id] = Math.round(b[id].t / b[id].c); });
        return row;
      })
      .sort((a, b) => a.ts - b.ts);

    return { rows, series };
  }, [filteredPrices, trendPick, trendGran, trendWeeks, commodities, retiredIds]);

  // Latest record date across currently filtered prices.
  const latestFilteredDate = useMemo(() => {
    let max = null;
    for (const p of filteredPrices) if (!max || p.date > max) max = p.date;
    return max;
  }, [filteredPrices]);

  // ── Market comparison - like-for-like ───────────────────────────────────────
  // Ranking markets by "average price of everything recorded there" punishes
  // markets that happen to trade pricier commodities. Comparison is only fair
  // one commodity at a time, in ₦/kg. Default = highest-coverage commodity.
  const [compCommodity, setCompCommodity] = useState(''); // '' = auto

  const compEffectiveId = useMemo(() => {
    if (compCommodity) return compCommodity;
    const counts = {};
    filteredPrices.forEach((p) => { counts[p.commodityId] = (counts[p.commodityId] || 0) + 1; });
    const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return (ranked.find(([id]) => !retiredIds.has(id)) || ranked[0])?.[0] || '';
  }, [compCommodity, filteredPrices, retiredIds]);

  const compCommodityName = (commodities.find((c) => c.id === compEffectiveId)?.name || '-') + (retiredIds.has(compEffectiveId) ? ' (retired)' : '');

  const marketComparisonData = useMemo(() => {
    if (!compEffectiveId) return [];
    const byMarket = {};
    filteredPrices.forEach((price) => {
      if (price.commodityId !== compEffectiveId) return;
      const market = markets.find((m) => m.id === price.marketId);
      if (!market) return;
      if (!byMarket[market.id]) {
        byMarket[market.id] = { marketId: market.id, name: market.name, state: market.state, totalPerKg: 0, count: 0 };
      }
      byMarket[market.id].totalPerKg += price.pricePerKg || (price.price / (price.bagSize || 100));
      byMarket[market.id].count += 1;
    });
    return Object.values(byMarket)
      .map((m) => ({ ...m, avgPrice: Math.round(m.totalPerKg / m.count) }))
      .sort((a, b) => b.avgPrice - a.avgPrice)
      .slice(0, 10);
  }, [filteredPrices, compEffectiveId, markets]);

  // Historical price data for a specific market + commodity, with an optional
  // dashed "state average" series (same commodity, every market in the state)
  // so a market instantly reads as running hot or cold for its region.
  const [histCompare, setHistCompare] = useState(false);

  const histPriceData = useMemo(() => {
    const rows = localPrices
      .filter((p) => p.marketId === histMarket && p.commodityId === histCommodity)
      .map((p) => ({
        date: p.date.slice(5),
        fullDate: p.date,
        ts: new Date(p.date).getTime(),
        price: priceUnit === 'kg' ? p.pricePerKg : p.price,
        bagSize: p.bagSize,
      }))
      .sort((a, b) => a.ts - b.ts);

    if (histCompare && rows.length > 0) {
      const state = markets.find((m) => m.id === histMarket)?.state;
      const stateMarketIds = new Set(markets.filter((m) => m.state === state).map((m) => m.id));
      const byDate = {};
      localPrices.forEach((p) => {
        if (p.commodityId !== histCommodity || !stateMarketIds.has(p.marketId)) return;
        // Always average per kg: markets sell different bag sizes for the
        // same commodity, so raw bag prices are not comparable across a state.
        // A row with no per-kg figure is skipped rather than counted as ₦0,
        // which used to drag the dashed state average below every market on
        // the chart.
        if (p.pricePerKg == null) return;
        if (!byDate[p.date]) byDate[p.date] = { t: 0, c: 0 };
        byDate[p.date].t += p.pricePerKg;
        byDate[p.date].c += 1;
      });
      rows.forEach((r) => {
        const cell = byDate[r.fullDate];
        if (cell?.c) {
          const avgKg = cell.t / cell.c;
          // In Bag view, convert the per-kg average using THIS market's bag
          // size so the dashed line stays like-for-like with the solid line.
          r.stateAvg = Math.round(priceUnit === 'kg' ? avgKg : avgKg * r.bagSize);
          // Sample size behind the average - surfaced in the chart tooltip so
          // a week where only this market reported explains itself.
          r.stateAvgN = cell.c;
        }
      });
    }
    return rows;
  }, [histMarket, histCommodity, priceUnit, localPrices, histCompare, markets]);

  // Period stats for the historical view.
  const histStats = useMemo(() => {
    if (histPriceData.length === 0) return null;
    // Nulls out before Math.max/min, where a single one reads as 0 and reports
    // a period low of ₦0 beside real prices.
    const vals = histPriceData.map((r) => r.price).filter((v) => v != null);
    if (vals.length === 0) return null;
    const first = vals[0];
    const last = vals[vals.length - 1];
    return {
      latest: last,
      high: Math.max(...vals),
      low: Math.min(...vals),
      pct: first ? ((last - first) / first) * 100 : null,
    };
  }, [histPriceData]);

  const histMarketName = markets.find((m) => m.id === histMarket)?.name || 'Market';
  const histMarketState = markets.find((m) => m.id === histMarket)?.state || 'the state';
  const histCommodityName = (commodities.find((c) => c.id === histCommodity)?.name || 'Commodity') + (retiredIds.has(histCommodity) ? ' (retired)' : '');

  // Historical select OPTIONS follow the page scope (the sticky filter bar):
  // scope up top, lens down here - the two layers cooperate instead of fighting.
  const histMarketOptions = useMemo(() => {
    let opts = markets;
    if (selectedMarkets.length > 0) opts = opts.filter((m) => selectedMarkets.includes(m.id));
    else if (selectedStates.length > 0) opts = opts.filter((m) => marketInStates(m, selectedStates));
    return opts;
  }, [markets, selectedMarkets, selectedStates]);

  // Commodities available in the selected historical market (∩ page categories).
  const commoditiesInMarket = useMemo(() => {
    const ids = new Set(localPrices.filter((p) => p.marketId === histMarket).map((p) => p.commodityId));
    return commodities.filter((c) => ids.has(c.id) && (selectedCategories.length === 0 || selectedCategories.includes(c.category)));
  }, [histMarket, localPrices, commodities, selectedCategories]);

  // Keep the historical selections valid as the page scope changes.
  useEffect(() => {
    if (histMarketOptions.length > 0 && !histMarketOptions.some((m) => m.id === histMarket)) {
      setHistMarket(histMarketOptions[0].id);
    }
  }, [histMarketOptions, histMarket]);
  useEffect(() => {
    if (commoditiesInMarket.length > 0 && !commoditiesInMarket.some((c) => c.id === histCommodity)) {
      setHistCommodity(commoditiesInMarket[0].id);
    }
  }, [commoditiesInMarket, histCommodity]);

  // ── Price-records rows (resolved · sorted · paged) ──────────────────────────
  const priceRows = useMemo(
    () =>
      filteredPrices.map((price) => {
        const market = markets.find((m) => m.id === price.marketId);
        const commodity = commodities.find((c) => c.id === price.commodityId);
        return {
          id: price.id,
          date: price.date,
          market: market?.name || 'Unknown',
          state: market?.state || '-',
          commodity: commodity?.name || 'Unknown',
          retired: commodity ? retiredIds.has(commodity.id) : false,
          bagSize: price.bagSize,
          price: priceUnit === 'kg' ? price.pricePerKg : price.price,
        };
      }),
    [filteredPrices, markets, commodities, retiredIds, priceUnit]
  );

  const sortedRows = useMemo(() => {
    const arr = [...priceRows];
    arr.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      let cmp;
      if (typeof av === 'string' && typeof bv === 'string') {
        cmp = orderBy === 'date' ? av.localeCompare(bv) : av.localeCompare(bv, undefined, { sensitivity: 'base' });
      } else {
        cmp = (av ?? 0) - (bv ?? 0);
      }
      return order === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [priceRows, orderBy, order]);

  const pagedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  const handleSort = (col) => {
    const isAsc = orderBy === col && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(col);
    setPage(0);
  };

  // CSV export is a PREMIUM privilege: visitors see a locked button that opens
  // a sign-in prompt; signed-in premium/admin users download directly.
  const { user, isPremium, isAdmin } = useAuth();
  const canExport = Boolean(user) && (isPremium || isAdmin);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Export goes through the server so it counts against the plan allowance:
  // a download that skipped the meter would let anyone with an account pull
  // the archive for free. Rows are the CURRENT filtered and sorted set, every
  // page of it, not just the page on screen.
  const exportHeaders = ['Date', 'Market', 'State', 'Commodity', 'Bag Size (kg)', `Price (${priceUnit === 'kg' ? 'per kg' : 'per bag'})`, 'Price per kg (NGN)'];
  const exportRows = (() => {
    const perKgById = new Map(
      filteredPrices.map((p) => {
        const v = perKg(p);
        return [p.id, v == null ? null : Math.round(v)];
      }),
    );
    // An empty cell, never a 0, for a figure the row does not carry: a
    // downloaded CSV is the one place a made up zero cannot be corrected later.
    return sortedRows.map((r) => [r.date, r.market, r.state, r.commodity, r.bagSize, r.price ?? '', perKgById.get(r.id) ?? '']);
  })();

  // ── Shared styling ──────────────────────────────────────────────────────────
  const unitSuffix = priceUnit === 'kg' ? 'per kg' : 'per bag';

  const cardSx = {
    bgcolor: palette.card,
    border: `1px solid ${palette.cardBorder}`,
    borderRadius: `${radius.card}px`,
    boxShadow: palette.shadowSm,
  };

  // "Updated · date" stamp (matches Home's tsSx).
  const tsSx = { fontSize: '0.64rem', color: palette.textMuted, fontStyle: 'italic', mt: 0.6, letterSpacing: '0.02em' };

  // Chart primitives - F axis / grid / navy tooltip (from Home).
  const axisSx = { tick: { fontSize: 11, fill: palette.chartAxis, fontFamily: MONO }, axisLine: false, tickLine: false };
  const gridSx = { stroke: palette.chartGrid, strokeDasharray: '3 3' };
  const tt = {
    contentStyle: { backgroundColor: palette.tooltipBg, border: `1px solid ${palette.tooltipBorder}`, borderRadius: 10, fontSize: '0.8rem', padding: '10px 14px', color: palette.tooltipText },
    labelStyle: { fontWeight: 600, color: palette.accentLight, marginBottom: 4 },
    itemStyle: { color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem' },
  };

  // Filter-control skins.
  const controlSx = {
    bgcolor: palette.card,
    borderRadius: `${radius.control}px`,
    fontSize: '0.86rem',
    color: palette.text,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.cardBorder },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.textMuted },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: palette.accent, boxShadow: `0 0 0 3px ${palette.accentBg}` },
    '& .MuiSvgIcon-root': { color: palette.textMuted },
  };
  const labelSx = { color: palette.textSecondary, fontWeight: 600, '&.Mui-focused': { color: palette.accent } };
  const menuProps = {
    PaperProps: {
      sx: {
        maxHeight: 340,
        mt: 0.5,
        borderRadius: `${radius.control}px`,
        border: `1px solid ${palette.cardBorder}`,
        boxShadow: palette.shadowLg,
        '& .MuiMenuItem-root': { py: 0.25 },
      },
    },
  };
  const checkboxSx = { color: palette.textMuted, '&.Mui-checked': { color: palette.accent } };

  const toggleSx = {
    '& .MuiToggleButton-root': {
      fontSize: '0.72rem',
      fontWeight: 600,
      textTransform: 'none',
      color: palette.textSecondary,
      borderColor: palette.cardBorder,
      '&.Mui-selected': {
        backgroundColor: palette.accentBg,
        color: palette.accent,
        '&:hover': { backgroundColor: palette.accentBg },
      },
    },
  };

  const filterChipSx = {
    fontSize: '0.75rem',
    fontWeight: 600,
    height: 26,
    bgcolor: palette.surface,
    color: palette.text,
    border: `1px solid ${palette.cardBorder}`,
    '& .MuiChip-deleteIcon': { color: palette.textMuted, fontSize: 16, '&:hover': { color: palette.danger } },
  };

  const headCellSx = {
    fontWeight: 700,
    fontSize: '0.68rem',
    color: palette.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    bgcolor: palette.surface,
    borderBottom: `1px solid ${palette.divider}`,
    py: 1.4,
    whiteSpace: 'nowrap',
  };
  const sortLabelSx = {
    color: `${palette.textMuted} !important`,
    '&:hover': { color: `${palette.text} !important` },
    '&.Mui-active': { color: `${palette.text} !important` },
    '&.Mui-active .MuiTableSortLabel-icon': { color: `${palette.accent} !important` },
  };
  const paginationSx = {
    color: palette.textSecondary,
    '& .MuiTablePagination-toolbar': { minHeight: 44, pl: 0 },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { ...numericSx, fontSize: '0.78rem', color: palette.textSecondary },
    '& .MuiTablePagination-select': { ...numericSx, fontSize: '0.78rem' },
    '& .MuiSvgIcon-root': { color: palette.textMuted },
  };

  // renderValue for a multi-select: "All …" · single label · "N …".
  const renderMulti = (sel, plural, labelFn) => {
    const empty = !sel || sel.length === 0;
    const text = empty ? `All ${plural}` : sel.length === 1 ? labelFn(sel[0]) : `${sel.length} ${plural}`;
    return <Box component="span" sx={{ fontSize: '0.86rem', color: empty ? palette.textMuted : palette.text }}>{text}</Box>;
  };

  // Dashed empty-state box (Home style).
  const EmptyBox = ({ title, hint }) => (
    <Box sx={{ py: 6, px: 3, textAlign: 'center', border: `1px dashed ${palette.cardBorder}`, borderRadius: `${radius.control}px`, bgcolor: palette.surface }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: palette.text }}>{title}</Typography>
      {hint && <Typography sx={{ fontSize: '0.8rem', color: palette.textMuted, mt: 0.5, maxWidth: 340, mx: 'auto', lineHeight: 1.5 }}>{hint}</Typography>}
    </Box>
  );

  // Chart-card header block.
  const ChartHeader = ({ title, subtitle, stamp }) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontWeight: 700, color: palette.text, fontSize: '1rem', lineHeight: 1.3 }}>{title}</Typography>
      <Typography sx={{ color: palette.textMuted, fontSize: '0.78rem', mt: 0.25 }}>{subtitle}</Typography>
      {stamp && <Typography sx={tsSx}>Updated · {fmtDate(stamp)}</Typography>}
    </Box>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: palette.bg }}>
      {/* Hero (unchanged) */}
      <PublicHero
        eyebrow="Local Markets"
        title="Markets Across Nigeria"
        subtitle="Tracking commodity prices across local markets in Kaduna, Taraba, Katsina and Bauchi."
      />

      {/* ================================================================
          FILTER CONTROLS - sticky F card strip (blurred, hairline base)
          ================================================================ */}
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          background: palette.navBg,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${palette.navBorder}`,
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={1.75} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search commodities, markets…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: palette.textMuted }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: `${radius.control}px`,
                    bgcolor: palette.card,
                    color: palette.text,
                    fontSize: '0.86rem',
                    '& fieldset': { borderColor: palette.cardBorder },
                    '&:hover fieldset': { borderColor: palette.textMuted },
                    '&.Mui-focused fieldset': { borderColor: palette.accent, boxShadow: `0 0 0 3px ${palette.accentBg}` },
                  },
                  '& .MuiInputBase-input::placeholder': { color: palette.textMuted, opacity: 1 },
                }}
              />
            </Grid>

            {/* State (multi) */}
            <Grid item xs={6} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel shrink id="mkt-state-label" sx={labelSx}>State</InputLabel>
                <Select
                  labelId="mkt-state-label"
                  multiple
                  displayEmpty
                  value={selectedStates}
                  onChange={(e) => handleStatesChange(e.target.value)}
                  input={<OutlinedInput notched label="State" sx={controlSx} />}
                  renderValue={(sel) => renderMulti(sel, 'states', stateLabel)}
                  MenuProps={menuProps}
                >
                  {stateOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Checkbox size="small" checked={selectedStates.indexOf(opt.value) > -1} sx={checkboxSx} />
                      <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '0.86rem' }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Market (multi) */}
            <Grid item xs={6} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel shrink id="mkt-market-label" sx={labelSx}>Market</InputLabel>
                <Select
                  labelId="mkt-market-label"
                  multiple
                  displayEmpty
                  value={selectedMarkets}
                  onChange={(e) => handleMarketsChange(e.target.value)}
                  input={<OutlinedInput notched label="Market" sx={controlSx} />}
                  renderValue={(sel) => renderMulti(sel, 'markets', marketLabel)}
                  MenuProps={menuProps}
                >
                  {filteredMarkets.map((market) => (
                    <MenuItem key={market.id} value={market.id}>
                      <Checkbox size="small" checked={selectedMarkets.indexOf(market.id) > -1} sx={checkboxSx} />
                      <ListItemText primary={market.name} secondary={market.state} primaryTypographyProps={{ fontSize: '0.86rem' }} secondaryTypographyProps={{ fontSize: '0.72rem', color: palette.textMuted }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Category (multi) */}
            <Grid item xs={6} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel shrink id="mkt-cat-label" sx={labelSx}>Category</InputLabel>
                <Select
                  labelId="mkt-cat-label"
                  multiple
                  displayEmpty
                  value={selectedCategories}
                  onChange={(e) => handleCategoriesChange(e.target.value)}
                  input={<OutlinedInput notched label="Category" sx={controlSx} />}
                  renderValue={(sel) => renderMulti(sel, 'categories', categoryLabel)}
                  MenuProps={menuProps}
                >
                  {categoryOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Checkbox size="small" checked={selectedCategories.indexOf(opt.value) > -1} sx={checkboxSx} />
                      <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '0.86rem' }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Active filter chips - one per selected value + search */}
          {hasActiveFilters && (
            <Box sx={{ mt: 1.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: palette.textMuted, fontSize: '0.78rem', fontWeight: 600 }}>
                Active filters:
              </Typography>
              {selectedStates.map((v) => (
                <Chip key={`st-${v}`} size="small" label={stateLabel(v)} onDelete={() => removeState(v)} sx={filterChipSx} />
              ))}
              {selectedMarkets.map((v) => (
                <Chip key={`mk-${v}`} size="small" label={marketLabel(v)} onDelete={() => removeMarket(v)} sx={filterChipSx} />
              ))}
              {selectedCategories.map((v) => (
                <Chip key={`cat-${v}`} size="small" label={categoryLabel(v)} onDelete={() => removeCategory(v)} sx={filterChipSx} />
              ))}
              {searchTerm && (
                <Chip size="small" label={`“${searchTerm}”`} onDelete={() => setSearchTerm('')} sx={filterChipSx} />
              )}
              <Button size="small" onClick={clearFilters} sx={{ color: palette.accent, fontWeight: 600, fontSize: '0.78rem', '&:hover': { bgcolor: 'transparent', color: '#1C9550' } }}>
                Clear all
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* ================================================================
          4-TAB INTERFACE
          ================================================================ */}
      <Container maxWidth="lg" sx={{ mt: 4, pb: 8 }}>
        <MotionInView variants={fadeUp}>
          <Box sx={{ borderBottom: `1px solid ${palette.divider}`, mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': { fontSize: '0.9rem', color: palette.textSecondary, minHeight: 48 },
                '& .Mui-selected': { color: palette.accent },
                '& .MuiTabs-indicator': { backgroundColor: palette.accent },
              }}
            >
              <Tab label="Price Trends" />
              <Tab label="Market Comparison" />
              <Tab label="Historical Prices" />
              <Tab label="Price Records" />
            </Tabs>
          </Box>
        </MotionInView>

        {/* ── Tab 0: Price trends by commodity (₦/kg, per-series lines) ── */}
        {activeTab === 0 && (
          <MotionInView variants={fadeUp}>
            <Box sx={{ ...cardSx, p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <ChartHeader
                  title="Price trends by commodity"
                  subtitle="Average ₦ per kg within your current filters · one line per commodity"
                  stamp={latestFilteredDate}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  {/* Granularity */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {[{ v: 'W', l: 'Weekly' }, { v: 'M', l: 'Monthly' }].map((g) => (
                      <Button key={g.v} size="small" onClick={() => setTrendGran(g.v)}
                        sx={{ px: 1.2, py: 0.3, fontSize: '0.7rem', fontWeight: 600, borderRadius: '8px', color: trendGran === g.v ? '#fff' : palette.textMuted, bgcolor: trendGran === g.v ? palette.accent : 'transparent', border: `1px solid ${trendGran === g.v ? palette.accent : palette.cardBorder}`, '&:hover': { bgcolor: trendGran === g.v ? palette.accent : palette.surface } }}>
                        {g.l}
                      </Button>
                    ))}
                  </Box>
                  {/* Range */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {TREND_RANGES.map((r) => (
                      <RangeLockWrap key={r.label} days={r.weeks * 7}>
                      <Button size="small" onClick={() => setTrendWeeks(r.weeks)}
                        sx={{ minWidth: 38, px: 0.9, py: 0.3, fontFamily: MONO, fontSize: '0.68rem', fontWeight: 600, borderRadius: '8px', color: trendWeeks === r.weeks ? '#fff' : palette.textMuted, bgcolor: trendWeeks === r.weeks ? palette.accent : 'transparent', border: `1px solid ${trendWeeks === r.weeks ? palette.accent : palette.cardBorder}`, '&:hover': { bgcolor: trendWeeks === r.weeks ? palette.accent : palette.surface } }}>
                        {r.label}
                      </Button>
                      </RangeLockWrap>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Commodity picker - empty means "top 3 by data coverage" */}
              <Autocomplete
                multiple
                size="small"
                options={trendOptions}
                value={trendPick}
                onChange={(_, val) => setTrendPick(val.slice(0, 6))}
                getOptionLabel={(o) => retiredLabel(o)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} placeholder={trendPick.length === 0 ? 'Top 3 commodities by coverage - type to choose your own' : 'Add commodity (max 6)'} />
                )}
                sx={{ mt: 2, maxWidth: 620, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: palette.card, fontSize: '0.85rem' } }}
              />

              {trend.rows.length === 0 ? (
                <Box sx={{ mt: 2.5 }}>
                  <EmptyBox title="No data available" hint="Adjust your filters above to see commodity price trends." />
                </Box>
              ) : (
                <>
                  {/* Legend */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2, mb: 1 }}>
                    {trend.series.map((s) => (
                      <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
                        <Typography sx={{ fontSize: '0.72rem', color: palette.textSecondary, fontWeight: 500 }}>{s.name}</Typography>
                      </Box>
                    ))}
                    <Typography sx={{ ...numericSx, fontSize: '0.66rem', color: palette.textMuted, ml: 'auto', alignSelf: 'center' }}>
                      gaps = no field record that {trendGran === 'M' ? 'month' : 'week'}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 380, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trend.rows}>
                        <CartesianGrid {...gridSx} />
                        <XAxis
                          dataKey="ts" type="number" scale="time" domain={['dataMin', 'dataMax']}
                          {...axisSx}
                          tickFormatter={(ts) => {
                            const d = new Date(ts);
                            return trendGran === 'M'
                              ? d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
                              : d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
                          }}
                        />
                        <YAxis {...axisSx} tickFormatter={(v) => (v >= 1000 ? `₦${(v / 1000).toFixed(1)}k` : `₦${v}`)} />
                        <Tooltip
                          {...tt}
                          labelFormatter={(ts) => new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          formatter={(value, key) => [`₦${Number(value).toLocaleString()}/kg`, trend.series.find((s) => s.id === key)?.name || key]}
                        />
                        {trend.series.map((s) => (
                          <Line key={s.id} type="monotone" dataKey={s.id} stroke={s.color} strokeWidth={1.8}
                            connectNulls={false}
                            dot={trendGran === 'M' ? { r: 3, fill: s.color, strokeWidth: 0 } : false}
                            activeDot={{ r: 4.5, fill: s.color, strokeWidth: 0 }} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              )}
            </Box>
          </MotionInView>
        )}

        {/* ── Tab 1: Market Comparison (like-for-like, one commodity in ₦/kg) ── */}
        {activeTab === 1 && (
          <MotionInView variants={fadeUp}>
            <Box sx={{ ...cardSx, p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <ChartHeader
                  title="Market price comparison"
                  subtitle={`Average ₦ per kg of ${compCommodityName} · top ${marketComparisonData.length || 10} markets, like-for-like`}
                  stamp={latestFilteredDate}
                />
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel id="comp-commodity-label" sx={labelSx}>Commodity</InputLabel>
                  <Select
                    labelId="comp-commodity-label"
                    value={compEffectiveId}
                    label="Commodity"
                    onChange={(e) => setCompCommodity(e.target.value)}
                    MenuProps={menuProps}
                    sx={controlSx}
                  >
                    {trendOptions.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{retiredLabel(c)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {marketComparisonData.length === 0 ? (
                <EmptyBox title="No data available" hint="Adjust your filters above to compare market prices." />
              ) : (
                <Box sx={{ height: 400, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={marketComparisonData} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid {...gridSx} />
                      <XAxis type="number" {...axisSx} tickFormatter={(v) => (v >= 1000 ? `₦${(v / 1000).toFixed(1)}k` : `₦${v}`)} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fontSize: 11, fill: palette.textSecondary, fontFamily: MONO }}
                        axisLine={false}
                        tickLine={false}
                        width={110}
                      />
                      <Tooltip
                        {...tt}
                        cursor={{ fill: palette.accentBg }}
                        formatter={(value, _k, entry) => [`₦${Number(value).toLocaleString()}/kg · ${entry?.payload?.count ?? '-'} record${(entry?.payload?.count || 0) === 1 ? '' : 's'}`, compCommodityName]}
                      />
                      <Bar dataKey="avgPrice" fill={palette.accent} radius={[0, 6, 6, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>
          </MotionInView>
        )}

        {/* ── Tab 2: Historical Prices (market + commodity filter) ── */}
        {activeTab === 2 && (
          <MotionInView variants={fadeUp}>
            <SectionHeading
              eyebrow="Historical Prices"
              title={`${histCommodityName} in ${histMarketName}`}
              subtitle="Historical price changes for a specific commodity in a selected market."
              animate={false}
              sx={{ mb: 3 }}
            />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="hist-market-label" sx={labelSx}>Market</InputLabel>
                  <Select
                    labelId="hist-market-label"
                    value={histMarketOptions.some((m) => m.id === histMarket) ? histMarket : ''}
                    label="Market"
                    onChange={(e) => setHistMarket(e.target.value)}
                    MenuProps={menuProps}
                    sx={controlSx}
                  >
                    {histMarketOptions.map((m) => (
                      <MenuItem key={m.id} value={m.id}>{m.name} ({m.state})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="hist-commodity-label" sx={labelSx}>Commodity</InputLabel>
                  <Select
                    labelId="hist-commodity-label"
                    value={commoditiesInMarket.some((c) => c.id === histCommodity) ? histCommodity : ''}
                    label="Commodity"
                    onChange={(e) => setHistCommodity(e.target.value)}
                    MenuProps={menuProps}
                    sx={controlSx}
                  >
                    {commoditiesInMarket.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{retiredLabel(c)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted, mt: -1.5, mb: 3 }}>
              Options follow the filters above.
            </Typography>

            <Box sx={{ ...cardSx, p: 3 }}>
              {histPriceData.length > 0 ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: palette.text }}>
                        Price over time ({histPriceData.length} records)
                      </Typography>
                      <Typography sx={tsSx}>
                        Updated · {fmtDate(histPriceData[histPriceData.length - 1]?.fullDate)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <ToggleButtonGroup
                        value={priceUnit}
                        exclusive
                        onChange={(e, val) => { if (val !== null) setPriceUnit(val); }}
                        size="small"
                        sx={toggleSx}
                      >
                        <ToggleButton value="bag">Bag</ToggleButton>
                        <ToggleButton value="kg">KG</ToggleButton>
                      </ToggleButtonGroup>
                      <Button size="small" onClick={() => setHistCompare((v) => !v)}
                        sx={{ px: 1.4, py: 0.4, fontSize: '0.72rem', fontWeight: 600, borderRadius: '8px', color: histCompare ? '#fff' : palette.textMuted, bgcolor: histCompare ? palette.navy : 'transparent', border: `1px solid ${histCompare ? palette.navy : palette.cardBorder}`, '&:hover': { bgcolor: histCompare ? palette.navy : palette.surface } }}>
                        vs state average
                      </Button>
                      <MuiTooltip
                        arrow
                        enterTouchDelay={0}
                        title={`For each week we average the per-kg price of ${histCommodityName} across every ${histMarketState} market that reported it. In Bag view the average is converted using this market's bag size, so markets selling different bag sizes stay comparable. On weeks where only this market reported, the average equals its own price - the chart tooltip shows how many markets are behind each point.`}
                      >
                        <IconButton
                          size="small"
                          aria-label="How the state average is calculated"
                          sx={{ p: 0.4, color: palette.textMuted, '&:hover': { color: palette.text, bgcolor: palette.surface } }}
                        >
                          <Icon icon="ph:info" width={15} height={15} />
                        </IconButton>
                      </MuiTooltip>
                    </Box>
                  </Box>

                  {/* Period stat strip */}
                  {histStats && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3.5, alignItems: 'center', borderTop: `1px solid ${palette.divider}`, borderBottom: `1px solid ${palette.divider}`, py: 1.5, mb: 2.5 }}>
                      {[
                        { l: 'Latest', v: histStats.latest, color: palette.accent },
                        { l: 'Period high', v: histStats.high },
                        { l: 'Period low', v: histStats.low },
                      ].map((s) => (
                        <Box key={s.l}>
                          <Typography sx={{ fontSize: '0.64rem', color: palette.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{s.l}</Typography>
                          <Typography sx={{ ...numericSx, fontSize: '1rem', fontWeight: 600, color: s.color || palette.text }}>{formatPrice(s.v)}</Typography>
                        </Box>
                      ))}
                      {histStats.pct != null && (
                        <Box>
                          <Typography sx={{ fontSize: '0.64rem', color: palette.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Change over period</Typography>
                          <Box sx={{ mt: 0.3 }}><DeltaChip value={histStats.pct} size="sm" /></Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {histCompare && (
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Box sx={{ width: 14, height: 2, bgcolor: palette.accent, borderRadius: 1 }} />
                        <Typography sx={{ fontSize: '0.7rem', color: palette.textSecondary }}>{histMarketName}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Box sx={{ width: 14, height: 0, borderTop: `2px dashed ${palette.navy}` }} />
                        <Typography sx={{ fontSize: '0.7rem', color: palette.textSecondary }}>State average</Typography>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ height: { xs: 280, md: 380 } }}>
                    <ResponsiveContainer>
                      <LineChart data={histPriceData}>
                        <CartesianGrid {...gridSx} />
                        <XAxis
                          dataKey="ts" type="number" scale="time" domain={['dataMin', 'dataMax']}
                          {...axisSx}
                          tickFormatter={(ts) => new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}
                        />
                        <YAxis {...axisSx} tickFormatter={(v) => (v >= 1000 ? `₦${(v / 1000).toFixed(0)}k` : `₦${v}`)} />
                        <Tooltip
                          {...tt}
                          formatter={(value, key, item) => {
                            if (key !== 'stateAvg') return [formatPrice(value), histCommodityName];
                            const n = item?.payload?.stateAvgN;
                            return [formatPrice(value), n ? `State average (${n} market${n === 1 ? '' : 's'})` : 'State average'];
                          }}
                          labelFormatter={(ts) => new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        />
                        <Line
                          type="monotone"
                          dataKey="price"
                          stroke={palette.accent}
                          strokeWidth={2.2}
                          connectNulls={false}
                          dot={{ r: 3, fill: palette.accent, strokeWidth: 0 }}
                          activeDot={{ r: 5 }}
                        />
                        {histCompare && (
                          <Line
                            type="monotone"
                            dataKey="stateAvg"
                            stroke={palette.navy}
                            strokeWidth={1.8}
                            strokeDasharray="6 4"
                            connectNulls={false}
                            dot={false}
                            activeDot={{ r: 4, fill: palette.navy }}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              ) : (
                <EmptyBox
                  title="No price records found"
                  hint="Try selecting a different market or commodity combination."
                />
              )}
            </Box>
          </MotionInView>
        )}

        {/* ── Tab 3: Price Records (sortable · paginated) ── */}
        {activeTab === 3 && (
          <MotionInView variants={fadeUp}>
            <Box sx={{ ...cardSx, overflow: 'hidden' }}>
              {sortedRows.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <EmptyBox
                    title="No price records match your filters"
                    hint="Try adjusting the filters above to widen your search."
                  />
                </Box>
              ) : (
                <>
                  <TableContainer sx={{ maxHeight: 560 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {PRICE_COLUMNS.map((col) => (
                            <TableCell
                              key={col.id}
                              align={col.align}
                              sortDirection={orderBy === col.id ? order : false}
                              sx={headCellSx}
                            >
                              <TableSortLabel
                                active={orderBy === col.id}
                                direction={orderBy === col.id ? order : 'asc'}
                                onClick={() => handleSort(col.id)}
                                sx={sortLabelSx}
                              >
                                {col.id === 'price' ? `Price (${unitSuffix})` : col.label}
                              </TableSortLabel>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedRows.map((row) => (
                          <TableRow
                            key={row.id}
                            hover
                            sx={{
                              '&:hover': { bgcolor: palette.cardHover },
                              '& td': { borderBottom: `1px solid ${palette.divider}`, py: 1.3, fontSize: '0.85rem' },
                            }}
                          >
                            <TableCell>
                              <Typography sx={{ ...numericSx, fontSize: '0.8rem', color: palette.textMuted }}>{fmtDate(row.date)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: palette.text }}>{row.market}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.85rem', color: palette.textSecondary }}>{row.state}</Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: '0.85rem', color: palette.text }}>{row.commodity}</Typography>
                                {row.retired && (
                                  <Chip label="RETIRED" size="small" sx={{ ml: 0.75, height: 16, fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.06em', bgcolor: palette.surfaceHover, color: palette.textMuted }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ ...numericSx, fontSize: '0.8rem', color: palette.textSecondary }}>{row.bagSize}kg</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ ...numericSx, fontWeight: 700, fontSize: '0.85rem', color: palette.accent }}>{formatPrice(row.price)}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Footer strip: match count + pagination */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                      px: 2.5,
                      py: 1,
                      bgcolor: palette.surface,
                      borderTop: `1px solid ${palette.divider}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary }}>
                        <Box component="span" sx={{ ...numericSx, fontWeight: 700, color: palette.text }}>{sortedRows.length.toLocaleString()}</Box>
                        {` record${sortedRows.length === 1 ? '' : 's'} match your filters`}
                      </Typography>
                      <ToggleButtonGroup
                        value={priceUnit}
                        exclusive
                        onChange={(e, val) => { if (val !== null) setPriceUnit(val); }}
                        size="small"
                        sx={toggleSx}
                      >
                        <ToggleButton value="bag">Bag</ToggleButton>
                        <ToggleButton value="kg">KG</ToggleButton>
                      </ToggleButtonGroup>
                      {canExport ? (
                        <ExportButton
                          filename={`farmgate-price-records-${latestFilteredDate || 'export'}`}
                          label="Export CSV"
                          headers={exportHeaders}
                          rows={exportRows}
                          variant="text"
                          sx={{ px: 1.4, py: 0.35, fontSize: '0.74rem', fontWeight: 600, borderRadius: '8px', color: palette.text, bgcolor: '#fff', border: `1px solid ${palette.cardBorder}`, '&:hover': { borderColor: palette.textMuted, bgcolor: palette.surface } }}
                        />
                      ) : (
                        <Button size="small" onClick={() => setExportDialogOpen(true)}
                          startIcon={<Icon icon="ph:lock-simple" width={14} height={14} />}
                          sx={{ px: 1.4, py: 0.35, fontSize: '0.74rem', fontWeight: 600, borderRadius: '8px', color: palette.text, bgcolor: '#fff', border: `1px solid ${palette.cardBorder}`, '&:hover': { borderColor: palette.textMuted, bgcolor: palette.surface } }}>
                          Export CSV
                        </Button>
                      )}
                    </Box>
                    <TablePagination
                      component="div"
                      count={sortedRows.length}
                      page={page}
                      onPageChange={(e, p) => setPage(p)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                      rowsPerPageOptions={[25, 50, 100]}
                      sx={paginationSx}
                    />
                  </Box>
                </>
              )}
            </Box>
          </MotionInView>
        )}
      </Container>

      {/* Premium CSV export gate - visitors get a sign-in prompt, not the file */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: `${radius.panel}px`,
            bgcolor: palette.card,
            border: `1px solid ${palette.cardBorder}`,
            boxShadow: palette.shadowLg,
            maxWidth: 420,
            m: 2,
          },
        }}
      >
        <Box sx={{ p: { xs: 3, sm: 3.5 } }}>
          <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: palette.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Icon icon="ph:lock-simple" width={22} height={22} color={palette.accent} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: palette.text, mb: 1 }}>
            CSV export is a premium feature
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', color: palette.textSecondary, lineHeight: 1.6, mb: 3 }}>
            Sign in with a premium account to download the full filtered dataset. Browsing prices and charts stays free.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to="/login?redirect=/markets"
              variant="contained"
              disableElevation
              endIcon={<Icon icon="ph:arrow-right" width={15} height={15} />}
              sx={{ bgcolor: palette.accent, color: '#fff', fontWeight: 600, fontSize: '0.86rem', textTransform: 'none', px: 2.2, py: 0.9, borderRadius: '10px', boxShadow: '0 1px 2px rgba(16,24,40,0.16)', '&:hover': { bgcolor: '#1C9550' } }}
            >
              Sign In
            </Button>
            <Button
              onClick={() => setExportDialogOpen(false)}
              sx={{ color: palette.textSecondary, fontWeight: 600, fontSize: '0.86rem', textTransform: 'none', px: 2, py: 0.9, borderRadius: '10px', '&:hover': { bgcolor: palette.surface } }}
            >
              Not now
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default function PublicMarkets() {
  const m = useMarkets();
  const ps = useStates();
  const c = useCommodities();
  const cat = useCategories();
  const lp = useLocalPrices();
  if (m.isLoading || ps.isLoading || c.isLoading || cat.isLoading || lp.isLoading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <PublicHero
          eyebrow="Local Markets"
          title="Markets Across Nigeria"
          subtitle="Loading commodity prices across local Nigerian markets…"
        />
        <LoadingSkeleton variant="page" contained />
      </Box>
    );
  }
  return (
    <PublicMarketsInner
      markets={m.data || []}
      pilotStates={ps.data || []}
      commodities={c.data || []}
      commodityCategories={cat.data || []}
      localPrices={lp.data || []}
    />
  );
}
